/**
 * Record a customer receipt — `/finance/customer-receipts/new`.
 *
 * Creating a receipt records money that arrived; it does not post. The receipt
 * posts when it is first allocated, so the bank leg and the AR/on-account split
 * are decided by one transaction rather than two — which is why this form ends
 * by handing straight over to the allocation editor.
 */
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@erp/ui";

import { surfaceProblem } from "@/components/form";
import { useIdempotentMutation } from "@/features/shared/ops-api";
import { useCurrentBranch } from "@/lib/branch/branch-store";

import {
  AR_RECEIPT_METHODS,
  createCustomerReceipt,
  type ARReceiptMethod,
  type CreateCustomerReceiptRequest,
  type CustomerReceipt,
} from "../api";
import { AMOUNT_SCALE, normalizeDecimal, todayIsoDate } from "../decimal";
import {
  DateField,
  DecimalField,
  NoticeBanner,
  PageHeader,
  ProblemBanner,
  SelectField,
  TextField,
} from "../components/fields";
import { financeKeys, useBankAccountList, usePartyOptions } from "../queries";

const NONE = "__none__";

export interface CustomerReceiptNewPageProps {
  onCreated: (receiptId: string) => void;
  onCancel: () => void;
  canReadParties?: boolean;
  canReadBankAccounts?: boolean;
}

export function CustomerReceiptNewPage({
  onCreated,
  onCancel,
  canReadParties = true,
  canReadBankAccounts = true,
}: CustomerReceiptNewPageProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { branchId } = useCurrentBranch();

  const [partyId, setPartyId] = useState(NONE);
  const [currency, setCurrency] = useState("SAR");
  const [fxRate, setFxRate] = useState("1");
  const [amount, setAmount] = useState("");
  const [bankAccountId, setBankAccountId] = useState(NONE);
  const [method, setMethod] = useState<ARReceiptMethod>("bank_transfer");
  const [reference, setReference] = useState("");
  const [receivedOn, setReceivedOn] = useState(todayIsoDate);
  const [formError, setFormError] = useState<string | null>(null);

  const partiesQuery = usePartyOptions("customer", canReadParties);
  const bankAccountsQuery = useBankAccountList({ active: true }, 200);

  const partyOptions = useMemo(
    () => [
      { value: NONE, label: t("finance.common.choose", "Choose…") },
      ...(partiesQuery.data ?? []).map((party) => ({
        value: party.id,
        label: party.display_name,
      })),
    ],
    [partiesQuery.data, t],
  );

  const bankOptions = useMemo(
    () => [
      { value: NONE, label: t("finance.common.choose", "Choose…") },
      ...bankAccountsQuery.rows.map((account) => ({
        value: account.id,
        label: `${account.code} · ${account.name} (${account.currency})`,
      })),
    ],
    [bankAccountsQuery.rows, t],
  );

  const createMutation = useIdempotentMutation<CustomerReceipt, CreateCustomerReceiptRequest>({
    mutationFn: (body, key) => createCustomerReceipt(body, key),
    onSuccess: (receipt) => {
      void queryClient.invalidateQueries({ queryKey: financeKeys.customerReceipts.all });
      onCreated(receipt.id);
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const submit = () => {
    if (branchId === null) {
      setFormError(
        t(
          "finance.common.branchRequired",
          "Select an acting branch before creating an operational document.",
        ),
      );
      return;
    }
    if (partyId === NONE) {
      setFormError(t("finance.receipts.customerRequired", "Choose the customer."));
      return;
    }
    if (bankAccountId === NONE) {
      setFormError(
        t("finance.receipts.bankRequired", "Choose the bank account the money reached."),
      );
      return;
    }
    const wireAmount = normalizeDecimal(amount, { scale: AMOUNT_SCALE, allowZero: false });
    if (wireAmount === null) {
      setFormError(
        t(
          "finance.receipts.amountInvalid",
          "Enter the amount received, with at most four decimals.",
        ),
      );
      return;
    }
    const fx = normalizeDecimal(fxRate, { scale: 4, allowZero: false });
    if (fx === null) {
      setFormError(t("finance.common.fxRateInvalid", "Enter a positive exchange rate."));
      return;
    }
    setFormError(null);
    createMutation.mutate({
      branch_id: branchId,
      party_id: partyId,
      currency,
      fx_rate: fx,
      amount: wireAmount,
      bank_account_id: bankAccountId,
      method,
      received_on: receivedOn,
      ...(reference.trim() === "" ? {} : { reference: reference.trim() }),
    });
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <PageHeader
        title={t("finance.receipts.createTitle", "Record a customer receipt")}
        description={t(
          "finance.receipts.createSubtitle",
          "Recording the money creates a draft. It posts when you allocate it.",
        )}
        actions={
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t("actions.cancel")}
          </Button>
        }
      />

      {branchId === null ? (
        <NoticeBanner>
          {t(
            "finance.common.branchRequired",
            "Select an acting branch before creating an operational document.",
          )}
        </NoticeBanner>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("finance.receipts.detailsSection", "Receipt")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <SelectField
            id="receipt-party"
            label={t("finance.receipts.customer", "Customer")}
            value={partyId}
            onValueChange={setPartyId}
            options={partyOptions}
            disabled={!canReadParties}
          />
          <SelectField
            id="receipt-bank-account"
            label={t("finance.receipts.bankAccount", "Bank account")}
            value={bankAccountId}
            onValueChange={setBankAccountId}
            options={bankOptions}
            disabled={!canReadBankAccounts}
          />
          <SelectField
            id="receipt-method"
            label={t("finance.receipts.method", "Method")}
            value={method}
            onValueChange={(value) => setMethod(value as ARReceiptMethod)}
            options={AR_RECEIPT_METHODS.map((value) => ({
              value,
              label: t(`finance.receipts.methodOption.${value}`, value),
            }))}
          />
          <TextField
            id="receipt-currency"
            label={t("finance.common.currency", "Currency")}
            value={currency}
            onChange={(value) => setCurrency(value.toUpperCase())}
            ltr
            maxLength={3}
          />
          <DecimalField
            id="receipt-amount"
            label={t("finance.receipts.amount", "Amount received")}
            value={amount}
            onChange={setAmount}
            decimal={{ scale: AMOUNT_SCALE, allowZero: false }}
            invalidMessage={t(
              "finance.receipts.amountInvalid",
              "Enter the amount received, with at most four decimals.",
            )}
          />
          <DecimalField
            id="receipt-fx"
            label={t("finance.common.fxRate", "Exchange rate")}
            value={fxRate}
            onChange={setFxRate}
            decimal={{ scale: 4, allowZero: false }}
            invalidMessage={t("finance.common.fxRateInvalid", "Enter a positive exchange rate.")}
          />
          <DateField
            id="receipt-received-on"
            label={t("finance.receipts.receivedOn", "Received on")}
            value={receivedOn}
            onChange={setReceivedOn}
          />
          <TextField
            id="receipt-reference"
            label={t("finance.receipts.reference", "Reference")}
            value={reference}
            onChange={setReference}
            ltr
          />
        </CardContent>
      </Card>

      {formError === null ? null : <ProblemBanner>{formError}</ProblemBanner>}

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" loading={createMutation.isPending} onClick={submit}>
          {t("finance.receipts.create", "Record receipt")}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t("actions.cancel")}
        </Button>
      </div>
    </div>
  );
}
